import type { OnlineOrderStatus } from "./types";

const safePathSegment = (value: string) => {
  if (value === "." || value === "..") {
    throw new TypeError("Unsafe URL path segment");
  }
  return encodeURIComponent(value);
};

/** Every backend path this app uses, in one place. */
export const endpoints = {
  // Session, uploads, categories, staff/roles, profile and billing are reused
  // from the seller API on purpose (decision D13): a restaurant store is a
  // store, and duplicating those surfaces would be a second place for a session
  // bug to live.
  seller: {
    me: "/seller/auth/me",
    fcmTokens: "/seller/fcm-tokens",
    // The login route is PUBLIC (`PublicSellerAuthController` →
    // /api/public/seller/auth/login) — it has to be, since it is what mints the
    // session. Only the post-login routes (`me`, `logout`, `switch-store`) live
    // under the guarded /api/seller/auth prefix.
    login: "/public/seller/auth/login",
    logout: "/seller/auth/logout",
    password: "/seller/password",
    switchStore: "/seller/auth/switch-store",
    /** The store profile (`logoUrl` among it). Readable by any member; the QR sticker draws the logo from it. */
    store: "/seller/store",
    /** Signed upload intent for the store logo: `{ fileName, contentType, sizeBytes }` -> `{ uploadUrl, objectKey }`. */
    storeLogoUploads: "/seller/uploads/store-logo",

    // Staff & roles (the `/manage/staff` screen). These are the seller RBAC
    // routes verbatim — `SellerStoreRoleController` (`seller/roles`),
    // `SellerStoreMemberController` (`seller/members`) and
    // `SellerStoreInviteController` (`seller/invites`) — reused rather than
    // duplicated, per decision D13.
    roles: "/seller/roles",
    role: (id: string) => `/seller/roles/${id}`,
    /** Already filtered by `storeKind`; a RESTAURANT store gets the whole enum. */
    rolePermissions: "/seller/roles/permissions",
    members: "/seller/members",
    member: (id: string) => `/seller/members/${id}`,
    invites: "/seller/invites",
    invite: (id: string) => `/seller/invites/${id}`,
    // The link for ONE invite, behind `MEMBER_INVITE`. The list deliberately
    // carries no tokens — see the server's `StoreInviteListItem`.
    inviteLink: (id: string) => `/seller/invites/${id}/link`,

    // Storefront themes (`/manage/settings/storefront`). Reused from the
    // seller API (D13): the gallery is filtered server-side to the bound
    // store's `storeKind`, so a restaurant only ever sees restaurant themes.
    // There is no restaurant-specific theme API and there should not be one.
    themes: "/seller/themes",
    themeApply: (id: string) => `/seller/themes/${id}/apply`,
    /** The store's own theme — `{ live, draft, hasDraft, appliedThemeId, … }`. */
    theme: "/seller/theme",
    /** PATCH. Writes the DRAFT only; live is untouched until publish. */
    /*
     * Mints the short-lived JWT the buyer needs to render the DRAFT instead of
     * the live page. Same `SellerAuthGuard` as the draft/publish/discard trio
     * the portal already calls, so a restaurant session reaches it unchanged.
     */
    themePreviewToken: "/seller/theme/preview-token",
    themeDraft: "/seller/theme/draft",
    themePublish: "/seller/theme/publish",
    themeDiscard: "/seller/theme/discard",

    // Menu categories (`/manage/categories`). `SellerCategoryController`
    // verbatim — the same categories the buyer catalogue reads, not a
    // restaurant-specific copy (decision D13).
    categories: "/seller/categories",
    /** Edit is **PATCH**, delete is DELETE. There is no PUT on this route. */
    category: (id: string) => `/seller/categories/${id}`,

    // A menu item IS a `Product` (D2), so its photos and its categories are
    // saved through the seller product routes.
    /** `PATCH` with `{ categoryIds }` — the only route that PERSISTS a menu
     *  item's categories on update; see the note in `MenuItemDetailPage`. Also
     *  the `GET` this app reads a product's image gallery from. */
    product: (id: string) => `/seller/products/${id}`,
    /** Signed upload intent. `productId` is REQUIRED in the body. */
    productUploads: "/seller/uploads/products",
    /** **PATCH**, and a whole-array REPLACE. There is no POST here, and no
     *  per-image DELETE — removing an image means PATCHing without it. */
    productImages: (productId: string) =>
      `/seller/products/${productId}/images`,
  },
  restaurant: {
    settings: "/restaurant/settings",
    areas: "/restaurant/areas",
    area: (id: string) => `/restaurant/areas/${id}`,
    tables: "/restaurant/tables",
    table: (id: string) => `/restaurant/tables/${id}`,
    publicTableAccess: (storeSlug: string, tableCode: string) =>
      `/public/restaurant/stores/${safePathSegment(storeSlug)}/tables/${safePathSegment(tableCode)}/access`,
    priceTiers: "/restaurant/price-tiers",
    priceTier: (id: string) => `/restaurant/price-tiers/${id}`,
    addonGroups: "/restaurant/addon-groups",
    addonGroup: (id: string) => `/restaurant/addon-groups/${id}`,
    addons: "/restaurant/addons",
    addon: (id: string) => `/restaurant/addons/${id}`,
    menuItems: "/restaurant/menu-items",
    /** P7.4 — the sidebar «Menu» figure: the dishes on the menu. */
    menuItemsCount: "/restaurant/menu-items/count",
    menuItem: (id: string) => `/restaurant/menu-items/${id}`,
    menuItemAddonGroups: (productId: string) =>
      `/restaurant/menu-items/${productId}/addon-groups`,
    /** Detach: the route carries the group id as a second path segment. */
    menuItemAddonGroup: (productId: string, addonGroupId: string) =>
      `/restaurant/menu-items/${productId}/addon-groups/${addonGroupId}`,
    /** REPLACES this item's exceptions for ONE group; `[]` clears them. */
    menuItemAddonGroupOverrides: (productId: string, addonGroupId: string) =>
      `/restaurant/menu-items/${productId}/addon-groups/${addonGroupId}/overrides`,
    /** What each option costs THIS dish (D56). */
    menuItemAddonIngredients: (productId: string) =>
      `/restaurant/menu-items/${productId}/addon-ingredients`,
    /** REPLACES what ONE option costs this dish; `[]` restores the default. */
    menuItemAddonIngredients2: (productId: string, addonId: string) =>
      `/restaurant/menu-items/${productId}/addons/${addonId}/ingredients`,
    /**
     * How this restaurant takes a transfer (D44, B35).
     *
     * A SELLER route, not a `/restaurant/*` one, and deliberately so: the
     * InstaPay address is the store's own `StorePaymentOption`, which the
     * diner's phone already reads verbatim. Minting a restaurant-side copy
     * would give one store two addresses that could disagree. D13 closes the
     * seller PORTAL to a restaurant store, not the seller API — this app is
     * the intended consumer of it.
     */
    paymentOptions: "/seller/payment-options",
    paymentOption: (id: string) => `/seller/payment-options/${id}`,
    kitchenStations: "/restaurant/kitchen-stations",
    kitchenStation: (id: string) => `/restaurant/kitchen-stations/${id}`,
    kitchenStationFallback: "/restaurant/kitchen-stations/fallback",
    printingConfig: "/restaurant/printing/config",
    printers: "/restaurant/printing/printers",
    qzSetup: "/restaurant/printing/qz/setup",
    printer: (id: string) => `/restaurant/printing/printers/${id}`,
    stationPrinter: (stationId: string) =>
      `/restaurant/printing/stations/${stationId}/printer`,
    receiptPrinter: "/restaurant/printing/receipt-printer",
    printTestJobs: "/restaurant/printing/test-jobs",
    printDevices: "/restaurant/printing/devices",
    pairPrintDevice: "/restaurant/printing/devices/pair",
    rotatePrintDevice: (id: string) =>
      `/restaurant/printing/devices/${id}/rotate`,
    revokePrintDevice: (id: string) =>
      `/restaurant/printing/devices/${id}/revoke`,
    printDeviceSession: "/restaurant/printing/device/session",
    printDevicePresence: "/restaurant/printing/device/presence",
    claimPrintJobs: "/restaurant/printing/device/jobs/claim",
    pendingPrintCommands: "/restaurant/printing/device/commands/pending",
    completePrintCommand: (id: string) =>
      `/restaurant/printing/device/jobs/${id}/command-completed`,
    heartbeatPrintJob: (id: string) =>
      `/restaurant/printing/device/jobs/${id}/heartbeat`,
    renderPrintJob: (id: string, leaseId: string) =>
      `/restaurant/printing/device/jobs/${id}/render?leaseId=${encodeURIComponent(leaseId)}`,
    acknowledgePrintJob: (id: string) =>
      `/restaurant/printing/device/jobs/${id}/ack`,
    qzCertificate: "/restaurant/printing/device/qz/certificate",
    createQzDiscoverySession: "/restaurant/printing/qz/discovery-sessions",
    signQzDiscovery: "/restaurant/printing/device/qz/discovery/sign",
    signQzNetworkScan: "/restaurant/printing/device/qz/scan/sign",
    authorizeQzPrintJob: (id: string) =>
      `/restaurant/printing/device/jobs/${id}/qz/authorize`,
    signQzPrintJob: (id: string) =>
      `/restaurant/printing/device/jobs/${id}/qz/sign`,
    /**
     * 86 an item, or put it back (D32). `PUT { isAvailable }`, allowed for
     * `RESTAURANT_MENU_MANAGE` **or** `RESTAURANT_MENU_AVAILABILITY` — the
     * cashier's grant, checked server-side in the service because the two are
     * an either/or.
     */
    menuItemAvailability: (productId: string) =>
      `/restaurant/menu-items/${productId}/availability`,
    /**
     * Who 86'd this dish, and when (B12). `GET`, `RESTAURANT_MENU_MANAGE` —
     * a stricter gate than the toggle above on purpose: every cook can make the
     * call (D50), reviewing the calls is the back office's.
     *
     * A sub-resource rather than a field on the item read, exactly like
     * `purchases` below: the log is capped, and nothing else in a menu-item
     * response is.
     */
    menuItemAvailabilityHistory: (productId: string) =>
      `/restaurant/menu-items/${productId}/availability-history`,
    /**
     * What this dish has charged for its options, who set it and when (D55).
     * A sub-resource GET for the same reason `availability-history` is one:
     * the log is capped, and nothing else in a menu-item response is.
     */
    menuItemAddonOverrideHistory: (productId: string) =>
      `/restaurant/menu-items/${productId}/addon-override-history`,
    /**
     * A combo's component set (S16, D52). `GET` reads it; `PUT { components }`
     * REPLACES it, and `components: []` turns the item back into a normal dish.
     * Both `RESTAURANT_MENU_MANAGE` — what is IN a meal deal is a menu edit,
     * not a mid-service call like the 86 toggle above.
     */
    menuItemCombo: (productId: string) =>
      `/restaurant/menu-items/${productId}/combo`,
    /** PUT with a nullable stationId — "unassigned" is a real state (D21). */
    menuItemStation: (productId: string) =>
      `/restaurant/menu-items/${productId}/station`,
    /** Omit stationId for a single-screen kitchen; pass it for one section. */
    kitchenBoard: (stationId?: string) =>
      stationId
        ? `/restaurant/kitchen-board?stationId=${stationId}`
        : "/restaurant/kitchen-board",
    /** P7.1 — the floor's Kitchen tab: the board's count without its plates. */
    kitchenBoardTab: "/restaurant/kitchen-board/tab",
    kitchenTicket: (id: string) => `/restaurant/kitchen-tickets/${id}`,
    /**
     * What the kitchen may 86, and its stations (D50). `RESTAURANT_MENU_AVAILABILITY`
     * — deliberately NOT the back office's `menu-items` list, which is
     * `RESTAURANT_MENU_VIEW` and carries every item's price.
     */
    kitchenAvailability: "/restaurant/kitchen-availability",
    menuItemRecipe: (productId: string) =>
      `/restaurant/menu-items/${productId}/recipe`,
    recipeLine: (id: string) => `/restaurant/recipe-lines/${id}`,
    /**
     * Supplier records (B45, unit 5) — who a delivery was bought FROM.
     *
     * `RESTAURANT_COSTING_MANAGE`, the same grant as ingredients and purchases:
     * a supplier is reference data for the purchase form. There is deliberately
     * NO delete — a supplier is retired with `PUT { isActive: false }`, because
     * this row is the only record of who a tax invoice came from.
     */
    aggregators: "/restaurant/aggregators",
    /** P7.2 — the till's platform pickers: the list without its bill count. */
    aggregatorChoices: "/restaurant/aggregators/choices",
    aggregator: (id: string) => `/restaurant/aggregators/${id}`,
    /** P6b — the platforms' totals over a period, asked for on demand. */
    aggregatorTotals: (from: string, to: string) =>
      `/restaurant/aggregators/totals?from=${from}&to=${to}`,
    suppliers: "/restaurant/suppliers",
    supplier: (id: string) => `/restaurant/suppliers/${id}`,
    /**
     * Minimal purchasing (2026-10-02). All `RESTAURANT_COSTING_MANAGE`, except
     * `supplierMarkPaid` (COSTING_MANAGE AND `REPORT_VIEW`) and reading a photo
     * (`purchaseInvoicePhoto` GET — either grant). An order moves nothing;
     * receiving one is `purchases` below with an `orderId`.
     */
    ingredientOrders: "/restaurant/ingredient-orders",
    ingredientOrderSuggestions: "/restaurant/ingredient-orders/suggestions",
    cancelIngredientOrder: (id: string) =>
      `/restaurant/ingredient-orders/${id}/cancel`,
    supplierDeliveries: (id: string) => `/restaurant/suppliers/${id}/deliveries`,
    /**
     * Receiving a delivery — استلام بضاعة (D147). `RESTAURANT_DELIVERY_RECORD`
     * on every route (the photo routes take it OR `COSTING_MANAGE`; viewing
     * the photo also `REPORT_VIEW`). Cost-free reads; the save is one paper,
     * all or nothing, keyed by its client-minted UUIDv7.
     */
    deliveries: "/restaurant/deliveries",
    delivery: (id: string) => `/restaurant/deliveries/${id}`,
    deliveryIngredients: "/restaurant/deliveries/ingredients",
    deliverySuppliers: "/restaurant/deliveries/suppliers",
    deliveryPhotoUpload: (id: string) => `/restaurant/deliveries/${id}/photo/upload`,
    deliveryPhoto: (id: string) => `/restaurant/deliveries/${id}/photo`,
    supplierMarkPaid: (id: string) => `/restaurant/suppliers/${id}/mark-paid`,
    purchaseInvoicePhotoUpload: (purchaseId: string) =>
      `/restaurant/purchases/${purchaseId}/invoice-photo/upload`,
    purchaseInvoicePhoto: (purchaseId: string) =>
      `/restaurant/purchases/${purchaseId}/invoice-photo`,
    ingredients: "/restaurant/ingredients",
    /** P7.4 — the sidebar «Stock» figure: the ingredients out or low. */
    ingredientsReorderCount: "/restaurant/ingredients/reorder-count",
    ingredient: (id: string) => `/restaurant/ingredients/${id}`,
    /**
     * Call an ingredient finished, or put it back (B18, unit 6).
     *
     * The one route in this block NOT gated on `RESTAURANT_COSTING_MANAGE`
     * alone: it takes that grant OR `RESTAURANT_MENU_AVAILABILITY`, because the
     * founder's answer is that a COOK does this, and a cook holds neither the
     * ingredient list nor a price. The kitchen's own 86 screen writes here.
     */
    ingredientAvailability: (id: string) =>
      `/restaurant/ingredients/${id}/availability`,
    /**
     * Who called it finished, and when. Back to `RESTAURANT_COSTING_MANAGE`
     * ALONE, deliberately: this is the record kept on the people holding the
     * other grant, and a history read carries the permission of the screen it
     * opens from — here, the back office's ingredient list.
     */
    ingredientAvailabilityHistory: (id: string) =>
      `/restaurant/ingredients/${id}/availability-history`,
    /** Newest first, capped at 200 (P5.3); `limit` asks for fewer. */
    purchases: (ingredientId: string, limit?: number) =>
      `/restaurant/ingredients/${ingredientId}/purchases${limit === undefined ? "" : `?limit=${limit}`}`,
    /** ONE purchase — the supplier-return form's read (P5.3). */
    purchase: (purchaseId: string) => `/restaurant/purchases/${purchaseId}`,
    adjustments: (ingredientId: string) =>
      `/restaurant/ingredients/${ingredientId}/adjustments`,
    /**
     * The depletion EVENTS behind the sums — which plates ate this ingredient.
     * The sums stay where they are; this is the drill-in under them, on the
     * same grant as `adjustments` beside it.
     */
    depletions: (ingredientId: string) =>
      `/restaurant/ingredients/${ingredientId}/depletions`,
    reconcile: (ingredientId: string) =>
      `/restaurant/ingredients/${ingredientId}/reconcile`,
    /**
     * The inventory item card — كارت الصنف (D135, phase 7). `from`/`to` are
     * business dates (`YYYY-MM-DD`), both required, inclusive, ordered and at
     * most 366 days apart (else 400 `RESTAURANT_INVOICE_DATE_RANGE_INVALID`);
     * more than 5,000 rows after grouping → 422 `RESTAURANT_ITEM_CARD_TOO_LARGE`.
     * `RESTAURANT_COSTING_MANAGE`, like the ledgers beside it.
     */
    itemCard: (ingredientId: string, from: string, to: string) =>
      `/restaurant/ingredients/${ingredientId}/card?from=${from}&to=${to}`,
    /**
     * The plates behind one grouped kitchen row of the card: `date` is the
     * row's business date, `kind` one of COOKED | REMAKE_WASTE | COOK_REVERSAL,
     * `run` the row's `ref.issueRun` (phase 12, PA-6 — a day's kitchen issue is
     * split around the other movements; absent = the whole day). Oldest first,
     * capped at 1,000 with `truncated`.
     */
    itemCardIssues: (ingredientId: string, date: string, kind: string, run?: number) =>
      `/restaurant/ingredients/${ingredientId}/card/issues?date=${date}&kind=${kind}${run === undefined ? "" : `&run=${run}`}`,
    /**
     * Return part of a delivery to its supplier — مرتجع مشتريات (D142).
     * `RESTAURANT_COSTING_MANAGE`; body `RecordSupplierReturnBody` (client
     * UUIDv7 PK — a replay answers 201 with the stored row).
     */
    supplierReturns: (purchaseId: string) =>
      `/restaurant/purchases/${purchaseId}/returns`,
    /**
     * Stocktake — جرد (D143). One OPEN count per store; every route
     * `RESTAURANT_COSTING_MANAGE`. `stocktakes` POSTs `{ id }` to open one;
     * `stocktakeCurrent` answers the open count or `null`; `stocktakeList`
     * takes business dates (≤ 92 days, the reports' rule).
     */
    stocktakes: "/restaurant/stocktakes",
    stocktakeCurrent: "/restaurant/stocktakes/current",
    stocktakeList: (from: string, to: string) =>
      `/restaurant/stocktakes?from=${from}&to=${to}`,
    stocktake: (id: string) => `/restaurant/stocktakes/${id}`,
    /** PUT `{ countedQty }` counts (or re-counts) one line; DELETE clears it. */
    stocktakeLine: (id: string, ingredientId: string) =>
      `/restaurant/stocktakes/${id}/lines/${ingredientId}`,
    stocktakePost: (id: string) => `/restaurant/stocktakes/${id}/post`,
    stocktakeCancel: (id: string) => `/restaurant/stocktakes/${id}/cancel`,
    // Reads are plain REST; the no-CRUD rule governs mutations only.
    grid: "/restaurant/invoices/grid",
    /** The live bills at no table, as floor tiles (2026-10-03). `POS_USE`. */
    gridOffPremise: "/restaurant/invoices/grid/off-premise",
    /** P7.5 — every area and table by name, for the bill pages. */
    tablePlaces: "/restaurant/invoices/grid/places",
    /** P7.6 — the waiting lane's DRAFT/PENDING plates for these bills. */
    cashierLane: (invoiceIds: readonly string[]) =>
      `/restaurant/invoices/cashier-lane?ids=${invoiceIds.join(",")}`,
    /** P5-Q — cheques closed over two days ago, read when the list opens. */
    gridOlderCheques: "/restaurant/invoices/grid/older-cheques",
    /** Call waiter (D132): the store's OPEN diner asks, oldest first. `POS_USE`. */
    serviceRequests: "/restaurant/service-requests",
    /** Marks one ask done (who + when). `POS_USE`. Not an invoice command. */
    serviceRequestDone: (id: string) =>
      `/restaurant/service-requests/${encodeURIComponent(id)}/done`,
    invoices: "/restaurant/invoices",
    invoice: (id: string) => `/restaurant/invoices/${id}`,
    /** THE mutation surface. Every invoice change goes through this one route. */
    commands: "/restaurant/invoices/commands",
    /**
     * A diner's own claim to have paid this bill (D44).
     *
     * Read and reviewed, never created here: the claim is written by the
     * diner's phone on the public surface. Both routes are
     * `RESTAURANT_INVOICE_SETTLE` — agreeing money arrived is the same
     * authority as settling.
     */
    invoicePayments: (invoiceId: string) =>
      `/restaurant/invoices/${invoiceId}/payments`,
    reviewInvoicePayment: (invoiceId: string, paymentId: string) =>
      `/restaurant/invoices/${invoiceId}/payments/${paymentId}/review`,
    /** Active tiers for the start-order picker (`RESTAURANT_INVOICE_OPEN`). */
    posPriceTiers: "/restaurant/invoices/price-tiers",
    /** T6 / F29 (D150) — the last bill with exactly this phone; one or none. */
    customerSuggestion: (phone: string) =>
      `/restaurant/invoices/customer-suggestion?phone=${encodeURIComponent(phone)}`,
    /**
     * The customer's own requests for a bag (F24, §5.12 — plan C3). All on
     * `RESTAURANT_INVOICE_OPEN`, the rung that opens bills: the list (default
     * REQUESTED, lazily expiring on read), the badge's count, accept (which
     * opens, adds and fires the ordinary bill server-side and returns it),
     * reject, and the till's pause switch — deliberately NOT `PUT settings`,
     * which is the owner's rung.
     */
    onlineOrders: (status: OnlineOrderStatus = "REQUESTED", limit = 50) =>
      `/restaurant/online-orders?status=${status}&limit=${limit}`,
    onlineOrdersCount: "/restaurant/online-orders/count",
    onlineOrderAccept: (id: string) => `/restaurant/online-orders/${id}/accept`,
    onlineOrderReject: (id: string) => `/restaurant/online-orders/${id}/reject`,
    onlineOrdersPause: "/restaurant/online-orders/pause",
    /**
     * What one dish may be swapped out of, and into (D63).
     *
     * Behind `RESTAURANT_INVOICE_SUBSTITUTE`, which is in no preset below
     * OWNER — so this 403s for an ordinary waiter, and the swap button is
     * hidden rather than offered-and-refused.
     */
    posSubstitutionOptions: (productId: string) =>
      `/restaurant/invoices/substitutions/${productId}`,
    /** The spent-vs-gained report (phase 2). `from`/`to` are business dates
     *  (`YYYY-MM-DD`), both required, inclusive, capped at 92 days server-side.
     *  `compare: "previous"` (S8, B12) attaches the immediately preceding
     *  window of equal length; omitted, the payload carries `comparison: null`. */
    spendGain: (from: string, to: string, compare?: "previous") =>
      `/restaurant/reports/spend-gain?from=${from}&to=${to}${
        compare ? `&compare=${compare}` : ""
      }`,
    /** Per-item profitability (S1) — units, net revenue, realised cost off the
     *  depletion ledger and the menu-engineering quadrant, ranked. */
    itemProfitability: (from: string, to: string) =>
      `/restaurant/reports/item-profitability?from=${from}&to=${to}`,
    /** Voids & comps by staff (S3) — the audit read, per actor, own-sales
     *  denominator (`salesBasis: "ADDED_BY_NET"`, B17). */
    staffExceptions: (from: string, to: string) =>
      `/restaurant/reports/staff-exceptions?from=${from}&to=${to}`,
    /** Hour × weekday revenue/covers grid, plus per-table turn time (S4). */
    /** B117/Q5 — send-backs and their waste, on the operations rung. */
    reportRemakes: (from: string, to: string) =>
      `/restaurant/reports/remakes?from=${from}&to=${to}`,
    /** B124/D114 — where the time goes, on the operations rung. Business
     *  dates, capped at 92 days server-side. */
    reportServiceSpeed: (from: string, to: string) =>
      `/restaurant/reports/service-speed?from=${from}&to=${to}`,
    /** Founder decision 5 (2026-10-02) — one row per refund, on the
     *  operations rung. Business dates, capped at 92 days server-side. */
    reportRefunds: (from: string, to: string) =>
      `/restaurant/reports/refunds?from=${from}&to=${to}`,
    /** D134 — diners' feedback for a business-date period (≤ 92 days), the
     *  summary over the whole period and one page of rows, newest first.
     *  Operations rung. */
    feedback: (from: string, to: string, page: number, pageSize: number) =>
      `/restaurant/feedback?from=${from}&to=${to}&page=${page}&pageSize=${pageSize}`,
    dayparts: (from: string, to: string) =>
      `/restaurant/reports/dayparts?from=${from}&to=${to}`,
    /**
     * The INPUT side of the VAT return (F8, unit 5). `RESTAURANT_REPORT_VIEW`
     * like every other money read — deliberately not the grant that AUTHORS the
     * supplier rows it reads.
     */
    inputVat: (from: string, to: string) =>
      `/restaurant/reports/input-vat?from=${from}&to=${to}`,
    // Cash-drawer shift sessions (D23-D29). Plain REST, not invoice commands
    // (D29) — a shift is not invoice state, so it gets its own routes rather
    // than the command envelope.
    /** `{ id, openedAt } | null` — existence only, no money. `POS_USE`: the
     *  till needs "is a drawer open" to explain a settle refusal in advance. */
    shiftCurrent: "/restaurant/shifts/current",
    /**
     * `ShiftView | null` — THE DRAWER I MAY STILL COUNT AGAIN (B117/B3, D99).
     *
     * `SHIFT_MANAGE`, the rung of the recount it leads to, never the
     * `REPORT_VIEW` pair that guards shift history — which is why history could
     * display both counts and still refuse the cashier who made them.
     */
    shiftRecountable: "/restaurant/shifts/recountable",
    /**
     * B117/Q2 — the CALLER'S OWN closed drawers, newest first, with their
     * notes. `SHIFT_MANAGE`, and scoped to the caller server-side: there is no
     * seller parameter, because a route that took one would be a store-wide
     * drawer read wearing a narrow name.
     */
    shiftsMine: "/restaurant/shifts/mine",
    /** The live summary. `SHIFT_MANAGE`. */
    shiftSummary: "/restaurant/shifts/current/summary",
    /** `POST { openingFloat, label }`. Guarded by D95's TWO partial unique
     *  indexes — a second cashier on the same BOX loses as
     *  `RESTAURANT_DRAWER_ALREADY_OPEN` (carrying the label, because the remedy
     *  is a handover), and a cashier who already holds one loses as
     *  `RESTAURANT_SHIFT_ALREADY_OPEN`. */
    shiftOpen: "/restaurant/shifts/open",
    /**
     * `{ open: OpenDrawer[], knownLabels: string[] }` — who is on which box, and
     * the boxes this store has named (D95, Unit B). `POS_USE` and MONEY-FREE:
     * being offered a handover is not being shown somebody's takings.
     */
    shiftDrawers: "/restaurant/shifts/drawers",
    /**
     * `StaffMember[]` — this store's team as a picker, for the counterparty on a
     * custody movement (Unit E).
     *
     * `POS_USE` and two fields only: the person who has to name the manager who
     * took the drop is the cashier. `endpoints.seller.members` answers the richer
     * question (roles, contact details) and stays owner-gated.
     */
    staff: "/restaurant/staff",
    /**
     * `Approver[]` — D156: who may type their password over this till's void,
     * comp, discount or refund-to-stock — the owner and every member holding
     * `RESTAURANT_EXCEPTION_APPROVE`, minus the caller. `POS_USE`; `{ sellerId,
     * name }` and nothing else.
     */
    approvers: "/restaurant/approvers",
    /**
     * `TeamActivity` — per current member, the last recorded act and the last
     * seven business days as worked / exception / off (round 5). `MEMBER_VIEW`,
     * the grant the members list it decorates requires. No money, no reasons.
     */
    teamActivity: "/restaurant/team/activity",
    /**
     * `POST { name, phone }` → `CreatedDriverInvite` (driver backend B1). An
     * invite that can only ever grant the DRIVER role; `MEMBER_INVITE` or
     * `RESTAURANT_DRIVER_INVITE`. The answer carries the link to share — the
     * backend sends nothing; the restaurant shares it from its own WhatsApp.
     */
    driverInvites: "/restaurant/team/driver-invites",
    /*
     * Dispatch (driver backend B3 · P13; types in `./dispatchTypes.ts`).
     * The board (T1) for `DISPATCH_MANAGE` or `DISPATCH_CASH_CONFIRM`;
     * offer / reassign / withdraw for `DISPATCH_MANAGE`; the accept timeout
     * read by `DISPATCH_MANAGE` or `SETTINGS_MANAGE`, written by
     * `SETTINGS_MANAGE` only. Every mutation carries a client UUIDv7 `id`.
     */
    dispatchBoard: "/restaurant/dispatch/board",
    dispatchSettings: "/restaurant/dispatch/settings",
    dispatchAssignments: "/restaurant/dispatch/assignments",
    dispatchReassign: (assignmentId: string) =>
      `/restaurant/dispatch/assignments/${safePathSegment(assignmentId)}/reassign`,
    dispatchWithdraw: (assignmentId: string) =>
      `/restaurant/dispatch/assignments/${safePathSegment(assignmentId)}/withdraw`,
    /* B3 · P14: problems, the stranded-bag end (T16), and cash handovers. */
    dispatchResolveProblem: (assignmentId: string) =>
      `/restaurant/dispatch/assignments/${safePathSegment(assignmentId)}/problem/resolve`,
    dispatchEnd: (assignmentId: string) =>
      `/restaurant/dispatch/assignments/${safePathSegment(assignmentId)}/end`,
    dispatchHandover: (handoverId: string) =>
      `/restaurant/dispatch/handovers/${safePathSegment(handoverId)}`,
    dispatchHandoverConfirm: (handoverId: string) =>
      `/restaurant/dispatch/handovers/${safePathSegment(handoverId)}/confirm`,
    dispatchHandoverReject: (handoverId: string) =>
      `/restaurant/dispatch/handovers/${safePathSegment(handoverId)}/reject`,
    dispatchDriverShiftEnd: (driverSellerId: string) =>
      `/restaurant/dispatch/drivers/${safePathSegment(driverSellerId)}/shift/end`,
    /*
     * B4 · P16: the bill's tracking link. T9 mints it (or reads the one the
     * driver's accept minted) — idempotent, no body; T10 records that the till
     * shared it (`{ id, clientTimestamp }`). Both `DISPATCH_MANAGE`.
     */
    /**
     * B6 · P25 — driver settlements (T20–T22, plan D-41 … D-45) and the
     * InstaPay proof's 60-second link (T19; opened in a fresh tab, never stored).
     */
    dispatchSettlementsOpen: "/restaurant/dispatch/settlements/open",
    dispatchSettlements: "/restaurant/dispatch/settlements",
    dispatchSettlement: (settlementId: string) =>
      `/restaurant/dispatch/settlements/${safePathSegment(settlementId)}`,
    dispatchPaymentProofView: (assignmentId: string) =>
      `/restaurant/dispatch/assignments/${safePathSegment(assignmentId)}/payment-proof/view`,
    dispatchTrackingLink: (invoiceId: string) =>
      `/restaurant/dispatch/invoices/${safePathSegment(invoiceId)}/tracking-link`,
    dispatchTrackingLinkShared: (invoiceId: string) =>
      `/restaurant/dispatch/invoices/${safePathSegment(invoiceId)}/tracking-link/shared`,
    /**
     * The customer's public tracking read (B4, plan D-29): anonymous, the
     * token is the capability. Read only through `publicApiFetch` (no cookie,
     * no referrer).
     */
    publicDispatchTracking: (token: string) =>
      `/public/restaurant/dispatch-tracking/${safePathSegment(token)}`,
    /*
     * B5 · P18: the drivers and their documents (T11–T15; types in
     * `./dispatchTypes.ts`). Roster and details for `DISPATCH_MANAGE` or
     * `DRIVER_DOCUMENTS_VIEW` (document data only with the latter); opening a
     * file (POST — it writes the access log first), reviewing it and reading
     * the log need `DRIVER_DOCUMENTS_VIEW` (manager, owner).
     */
    drivers: "/restaurant/drivers",
    driver: (sellerId: string) =>
      `/restaurant/drivers/${safePathSegment(sellerId)}`,
    driverDocumentView: (sellerId: string, documentId: string) =>
      `/restaurant/drivers/${safePathSegment(sellerId)}/documents/${safePathSegment(documentId)}/view`,
    driverDocumentReview: (sellerId: string, documentId: string) =>
      `/restaurant/drivers/${safePathSegment(sellerId)}/documents/${safePathSegment(documentId)}/review`,
    driverDocumentViews: (sellerId: string) =>
      `/restaurant/drivers/${safePathSegment(sellerId)}/documents/views`,
    /*
     * The customer list (2026-10-25; types in `./customerTypes.ts`). List and
     * details need `RESTAURANT_CUSTOMER_VIEW` (owner, manager, cashier);
     * `PATCH …/block` with `{ isBlocked, reason? }` needs
     * `RESTAURANT_CUSTOMER_BLOCK` (owner, manager).
     */
    customers: (search: string, limit: number, offset: number) => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      return `/restaurant/customers?${params.toString()}`;
    },
    customer: (customerId: string) =>
      `/restaurant/customers/${safePathSegment(customerId)}`,
    customerBlock: (customerId: string) =>
      `/restaurant/customers/${safePathSegment(customerId)}/block`,
    /**
     * `POST { label, countedCash }` → the NEW `ShiftView` (Unit B).
     *
     * One transaction: the outgoing drawer is counted and closed, and a new one
     * opens on the same box under the caller with that count as its float. Called
     * by the INCOMING cashier — the count is what the two of them just agreed.
     */
    shiftTakeover: "/restaurant/shifts/takeover",
    /**
     * `POST { id, kind, category, amount, reason, counterpartySellerId? }` → the
     * refreshed `ShiftSummaryView`.
     *
     * Unit E: `counterpartySellerId` is REQUIRED for the seven custody categories
     * and REFUSED for the rest — the till decides which by the same predicate the
     * service does, so a movement form that shows the picker at the wrong moment
     * fails loudly rather than filing a wrong name.
     */
    shiftMovements: "/restaurant/shifts/current/movements",
    /**
     * D157 — the OPTIONAL receipt photo on a movement, after it is saved:
     * `POST …/photo/upload { contentType, sizeBytes }` mints a presigned PUT,
     * `PUT …/photo { objectKey }` records it (→ `ShiftMovementPhotoState`),
     * `GET …/photo` → `InvoicePhotoUrl`. Attach: the recorder or SHIFT_MANAGE;
     * replace: the owner only.
     */
    shiftMovementPhotoUpload: (id: string) =>
      `/restaurant/shift-movements/${id}/photo/upload`,
    shiftMovementPhoto: (id: string) =>
      `/restaurant/shift-movements/${id}/photo`,
    /** `POST { countedCash }` → the closed `ShiftView`. */
    shiftClose: "/restaurant/shifts/current/close",
    /**
     * `POST { countedCash }` → the amended `ShiftView` (Unit D).
     *
     * ONCE per drawer, and only on one counted BLIND. The first count is not in
     * the body and cannot be: it is what the cashier said before they knew, and a
     * writer that could touch it would erase the evidence the pair exists for.
     */
    /**
     * B117/Q1 — a MANAGER's own count of a drawer somebody else closed. Its own
     * route rather than a flag on the recount beside it, because it is a
     * different person's statement landing in different columns, and the server
     * refuses whichever of the two is not the caller's.
     */
    shiftManagerCount: (shiftId: string) =>
      `/restaurant/shifts/${shiftId}/manager-count`,
    /** B117/Q2 — add a note to a drawer. Append-only; there is no PATCH. */
    shiftNotes: (shiftId: string) => `/restaurant/shifts/${shiftId}/notes`,
    shiftRecount: (shiftId: string) => `/restaurant/shifts/${shiftId}/recount`,
    /** Closed shifts only, newest first. `REPORT_VIEW`. */
    shifts: (page: number, limit = 20) =>
      `/restaurant/shifts?page=${page}&limit=${limit}`,
    /** One closed shift's full detail. `REPORT_VIEW`. */
    shift: (id: string) => `/restaurant/shifts/${id}`,
    /**
     * The Z (day-close) report (D38–D39). `businessDate` omitted asks the
     * server for its own default — the store's CURRENT business day, "close
     * today". `REPORT_VIEW`.
     */
    dayClose: (businessDate: string | null) =>
      businessDate
        ? `/restaurant/reports/day-close?businessDate=${businessDate}`
        : "/restaurant/reports/day-close",
    /**
     * The invoice CSV export (D40) — same filter fields as invoice history's
     * own `invoiceFilterParams`, minus paging. `REPORT_VIEW`.
     */
    invoiceExport: (search: string) =>
      `/restaurant/reports/invoices/export?${search}`,
  },
} as const;
